'use client';

import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { BsTrophy } from 'react-icons/bs';
import { HiOutlineShieldCheck } from 'react-icons/hi';
import { AiOutlineThunderbolt } from 'react-icons/ai';
import { FiClock, FiHeart } from 'react-icons/fi';
import { BiCheckCircle, BiStar } from 'react-icons/bi';
import { HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import LouiseButton from '@/components/buttons/LouiseButton';

type Props = { locale: string };

const GrowWithLouiseBot = ({ locale }: Props) => {
  const { t } = useTranslation(locale);

  return (
    <section className="tw-pt-16 tw-pb-16 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
      <div className="tw-relative tw-overflow-hidden tw-bg-gradient-to-br tw-from-blue-900 tw-via-blue-800 tw-to-purple-900 tw-rounded-3xl tw-shadow-2xl">
        {/* Background pattern */}
        <div className="tw-absolute tw-inset-0 tw-opacity-10">
          <div
            className="tw-absolute tw-inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.2) 2px, transparent 0)',
              backgroundSize: '50px 50px',
            }}
          ></div>
        </div>

        {/* Floating elements */}
        <div className="tw-absolute tw-top-4 tw-right-4 tw-w-20 tw-h-20 tw-bg-white/10 tw-rounded-full tw-blur-xl"></div>
        <div className="tw-absolute tw-bottom-4 tw-left-4 tw-w-32 tw-h-32 tw-bg-purple-500/20 tw-rounded-full tw-blur-2xl"></div>

        <div className="tw-relative tw-z-10 tw-p-8 lg:tw-p-16 tw-text-center">
          {/* Badge */}
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-white/20 tw-backdrop-blur-sm tw-text-white tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <BsTrophy className="tw-w-4 tw-h-4 tw-text-yellow-400" />
            {t('Client/final-cta-badge')}
          </div>

          {/* Main content */}
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-white tw-mb-6 tw-font-['Lexend']">
            {t('GrowWithUs/key1')}
          </h2>

          <p className="tw-text-lg sm:tw-text-xl tw-text-blue-100 tw-mb-8 tw-max-w-3xl tw-mx-auto tw-font-['Lexend']">
            {t('Client/final-cta-description')}
          </p>

          {/* Features grid */}
          <div className="tw-grid sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-6 tw-mb-10 tw-max-w-4xl tw-mx-auto">
            <div className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-4 tw-border tw-border-white/20">
              <HiOutlineShieldCheck className="tw-w-8 tw-h-8 tw-text-green-400 tw-mb-2 tw-mx-auto" />
              <div className="tw-text-white tw-font-semibold tw-text-sm tw-font-['Lexend']">{t('Client/feature1')}</div>
            </div>
            <div className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-4 tw-border tw-border-white/20">
              <AiOutlineThunderbolt className="tw-w-8 tw-h-8 tw-text-yellow-400 tw-mb-2 tw-mx-auto" />
              <div className="tw-text-white tw-font-semibold tw-text-sm tw-font-['Lexend']">{t('Client/feature2')}</div>
            </div>
            <div className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-4 tw-border tw-border-white/20">
              <FiHeart className="tw-w-8 tw-h-8 tw-text-pink-400 tw-mb-2 tw-mx-auto" />
              <div className="tw-text-white tw-font-semibold tw-text-sm tw-font-['Lexend']">{t('Client/feature3')}</div>
            </div>
            <div className="tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-2xl tw-p-4 tw-border tw-border-white/20">
              <BiStar className="tw-w-8 tw-h-8 tw-text-blue-400 tw-mb-2 tw-mx-auto" />
              <div className="tw-text-white tw-font-semibold tw-text-sm tw-font-['Lexend']">{t('Client/feature4')}</div>
            </div>
          </div>

          {/* CTA Button */}
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center tw-mb-8">
            <LouiseButton color="white" cta={t('GrowWithUs/key2')} ctaTarget="register" locale={locale} />
            <LouiseButton
              color="transparent-text-light"
              icon={<HiOutlineChatBubbleLeftRight className="tw-w-5 tw-h-5" />}
              cta={t('Client/contact-sales')}
              ctaTarget="contact"
              locale={locale}
            />
          </div>

          {/* Support features */}
          <div className="tw-flex tw-flex-wrap tw-justify-center tw-items-center tw-gap-6 lg:tw-gap-10 tw-text-white/80">
            <div className="tw-flex tw-items-center tw-gap-2">
              <HiOutlineChatBubbleLeftRight className="tw-w-5 tw-h-5 tw-text-green-400" />
              <span className="tw-text-sm tw-font-['Lexend']">{t('SiteCustomerSupport/text-01')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <FiClock className="tw-w-5 tw-h-5 tw-text-green-400" />
              <span className="tw-text-sm tw-font-['Lexend']">{t('SiteCustomerSupport/text-02')}</span>
            </div>
            <div className="tw-flex tw-items-center tw-gap-2">
              <BiCheckCircle className="tw-w-5 tw-h-5 tw-text-green-400" />
              <span className="tw-text-sm tw-font-['Lexend']">{t('Client/no-credit-card')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrowWithLouiseBot;
